/**
 * SubdivisionLayoutNode - Generates position arrays from proportional subdivisions
 *
 * Inspired by Tidal Cycles' proportional notation. Subdivision arrays like
 * [1, 2, 1] express relative spacing — the node normalizes them and maps to
 * positions across a given width/height. Connect posX/posY to a Repeater.
 */

import { BaseNode } from '@nodes/BaseNode';
import { Domain } from '@nodes/Endpoint';
import { NodeDef } from '@runtime/IRuntime';
import { Payload } from '@graph/Payload';
import { params, internals } from '@/nodes/TypedEndpoints';

// ─────────────────────────────────────────────────────────────
// Algorithm
// ─────────────────────────────────────────────────────────────

interface SubdivisionResult {
  centers: number[];   // origin-centered cell centers
  widths: number[];    // absolute cell widths (normalized × span)
  edges: number[];     // all boundary edges (including outer perimeter, origin-centered)
}

/**
 * Compute centered positions, cell widths, and all boundary edges from
 * proportional subdivisions.
 *
 * Given divs [1,2,1] and span 1:
 *   normalized widths = [0.25, 0.5, 0.25]
 *   cumulative edges  = [0, 0.25, 0.75, 1.0]
 *   raw centers       = [0.125, 0.5, 0.875]
 *   origin-centered   = [-0.375, 0.0, 0.375]
 *   all edges         = [-0.5, -0.25, 0.25, 0.5]  (N+1 boundaries for N cells)
 */
function subdivide(divs: number[], span: number): SubdivisionResult {
  const sum = divs.reduce((a, b) => a + b, 0);
  if (sum === 0) return { centers: [0], widths: [span], edges: [-span / 2, span / 2] };

  const normalized = divs.map(d => d / sum);
  const widths = normalized.map(w => w * span);
  const centers: number[] = [];
  const allEdges: number[] = [];
  let cumulative = 0;

  for (const w of normalized) {
    centers.push((cumulative + w / 2) * span);
    cumulative += w;
    allEdges.push(cumulative * span);
  }

  const offset = span / 2;
  // Internal division edges only (exclude outer perimeter)
  const edges = allEdges.slice(0, -1).map(e => e - offset);

  return {
    centers: centers.map(c => c - offset),
    widths,
    edges,
  };
}

// ─────────────────────────────────────────────────────────────
// Endpoints
// ─────────────────────────────────────────────────────────────

const subdivisionEndpoints = {
  ...params({
    divX:   { dataType: 'array', value: [1], min: -1, max: 10, step: 1, length: 8, group: 'Divisions', info: 'Proportional column widths. Values < 0 are ignored, so sliding to -1 removes a division.' },
    divY:   { dataType: 'array', value: [1], min: -1, max: 10, step: 1, length: 8, group: 'Divisions', info: 'Proportional row heights. Values < 0 are ignored, so sliding to -1 removes a division.' },
    width:  { value: 1, min: 0.01, max: 1000, step: 0.01, group: 'Size', info: 'Total width of the grid in world units' },
    height: { value: 1, min: 0.01, max: 1000, step: 0.01, group: 'Size', info: 'Total height of the grid in world units' },
    count:      { isOutput: true, readonly: true, group: 'Cells', info: 'Total number of cells (cols × rows)' },
    edgeCountX: { isOutput: true, readonly: true, label: 'Edge Count X', group: 'Edges', info: 'Number of internal vertical division edges' },
    edgeCountY: { isOutput: true, readonly: true, label: 'Edge Count Y', group: 'Edges', info: 'Number of internal horizontal division edges' },
  }),
  ...internals({
    posX:        { dataType: 'array', isOutput: true, label: 'X', group: 'Cells', info: 'Origin-centered X position of each cell' },
    posY:        { dataType: 'array', isOutput: true, label: 'Y', group: 'Cells', info: 'Origin-centered Y position of each cell' },
    cellWidths:  { dataType: 'array', isOutput: true, label: 'Cell W', group: 'Cells', info: 'Absolute width of each cell' },
    cellHeights: { dataType: 'array', isOutput: true, label: 'Cell H', group: 'Cells', info: 'Absolute height of each cell' },
    edgesX:      { dataType: 'array', isOutput: true, label: 'Edges X', group: 'Edges', info: 'X positions of internal vertical division edges' },
    edgesY:      { dataType: 'array', isOutput: true, label: 'Edges Y', group: 'Edges', info: 'Y positions of internal horizontal division edges' },
  }),
};

// ─────────────────────────────────────────────────────────────
// Node
// ─────────────────────────────────────────────────────────────

class SubdivisionLayoutNode extends BaseNode<typeof subdivisionEndpoints> {

  initialize(): void {
    this.compute();
  }

  onParamChanged(_payload: Payload, _endpointId: string): void {
    this.compute();
  }

  private compute(): void {
    const divX = this.toNumberArray(this.params.divX.value);
    const divY = this.toNumberArray(this.params.divY.value);
    const width = this.params.width.value;
    const height = this.params.height.value;

    const subX = subdivide(divX.length > 0 ? divX : [1], width);
    const subY = subdivide(divY.length > 0 ? divY : [1], height);

    // Cartesian product: for each Y row, emit all X columns
    const posX: number[] = [];
    const posY: number[] = [];
    const cellWidths: number[] = [];
    const cellHeights: number[] = [];

    for (let row = 0; row < subY.centers.length; row++) {
      for (let col = 0; col < subX.centers.length; col++) {
        posX.push(subX.centers[col]);
        posY.push(subY.centers[row]);
        cellWidths.push(subX.widths[col]);
        cellHeights.push(subY.widths[row]);
      }
    }

    this.internals.posX.set(posX);
    this.internals.posY.set(posY);
    this.internals.cellWidths.set(cellWidths);
    this.internals.cellHeights.set(cellHeights);
    this.internals.edgesX.set(subX.edges);
    this.internals.edgesY.set(subY.edges);
    this.params.count.set(posX.length);
    this.params.edgeCountX.set(subX.edges.length);
    this.params.edgeCountY.set(subY.edges.length);
  }

  private toNumberArray(value: unknown): number[] {
    if (!Array.isArray(value)) return [];
    return value.filter(v => typeof v === 'number' && isFinite(v) && v >= 0);
  }
}

// ─────────────────────────────────────────────────────────────
// Node Definition
// ─────────────────────────────────────────────────────────────

export const subdivisionLayoutNodeDef: NodeDef<SubdivisionLayoutNode> = {
  type: 'SubdivisionLayout',
  domain: Domain.Data,
  shortName: 'subdiv',
  info: 'Generates a grid from proportional subdivisions. divX/divY express relative column/row sizes — the node normalizes them into cell positions and widths.',
  category: 'Layout',
  endpoints: subdivisionEndpoints,
  ctor: SubdivisionLayoutNode,
};
